/*
  Lustrabella Shopify theme performance patch
  Load this BEFORE theme.js.

  Main goals:
  - Lazy-load product recommendations
  - Lazy-load recently viewed products
  - Leave cart and product interactions on the browser's native fetch path
*/

(() => {
  "use strict";

  if (window.__lustrabellaThemePerformancePatchLoaded) {
    return;
  }

  window.__lustrabellaThemePerformancePatchLoaded = true;

  const nativeFetch = window.fetch.bind(window);
  const nativeCustomElementsDefine = window.customElements.define.bind(window.customElements);
  const nativeCustomElementsGet = window.customElements.get.bind(window.customElements);

  function runWhenIdle(callback, timeout = 2500) {
    if ("requestIdleCallback" in window) {
      return window.requestIdleCallback(callback, { timeout });
    }

    return window.setTimeout(callback, Math.min(timeout, 1500));
  }

  function runWhenNearViewport(element, callback, rootMargin = "800px 0px") {
    if (!element) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      runWhenIdle(callback, 1500);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isNearViewport = entries.some((entry) => entry.isIntersecting);

        if (!isNearViewport) {
          return;
        }

        observer.disconnect();
        runWhenIdle(callback, 1200);
      },
      { rootMargin }
    );

    observer.observe(element);
  }

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function extractSectionId(element) {
    const section = element?.classList?.contains("shopify-section")
      ? element
      : element?.closest?.(".shopify-section");

    if (!section?.id) {
      return "";
    }

    return section.id.replace("shopify-section-", "");
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  async function lazyLoadProductRecommendations(element) {
    if (!element || element.__lustrabellaRecommendationsLoaded) {
      return;
    }

    element.__lustrabellaRecommendationsLoaded = true;

    const section = element.closest(".shopify-section");
    const sectionId = extractSectionId(section);
    const productId = element.getAttribute("product");
    const limit = element.getAttribute("limit") || "4";
    const intent = element.getAttribute("intent") || "related";

    if (!section || !sectionId || !productId) {
      return;
    }

    const url = `${getShopifyRoot()}recommendations/products?product_id=${encodeURIComponent(
      productId
    )}&limit=${encodeURIComponent(limit)}&section_id=${encodeURIComponent(
      sectionId
    )}&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await nativeFetch(url, {
        priority: intent === "related" ? "low" : "auto"
      });

      if (!response.ok) {
        return;
      }

      const tempDoc = parseHtml(await response.text());
      const newRecommendations = tempDoc.querySelector("product-recommendations");

      if (newRecommendations && newRecommendations.childElementCount > 0) {
        element.replaceChildren(...document.importNode(newRecommendations, true).childNodes);
        element.hidden = false;
      } else {
        element.remove();
      }
    } catch (error) {
      console.warn("[Lustrabella patch] Product recommendations failed to load.", error);
    }
  }

  async function lazyLoadRecentlyViewedProducts(element) {
    if (!element || element.__lustrabellaRecentlyViewedLoaded) {
      return;
    }

    element.__lustrabellaRecentlyViewedLoaded = true;

    const section = element.closest(".shopify-section");
    const sectionId = extractSectionId(section);

    if (!section || !sectionId) {
      return;
    }

    let viewedProductIds = [];

    try {
      viewedProductIds = Array.from(
        new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"))
      );
    } catch (error) {
      viewedProductIds = [];
    }

    if (element.hasAttribute("exclude-id")) {
      viewedProductIds = viewedProductIds.filter((id) => {
        return parseInt(id) !== parseInt(element.getAttribute("exclude-id"));
      });
    }

    const productsCount = parseInt(element.getAttribute("products-count") || "4");

    const query = viewedProductIds
      .slice(0, productsCount)
      .map((id) => `id:${id}`)
      .join(" OR ");

    if (!query) {
      section.remove();
      return;
    }

    const url = `${getShopifyRoot()}search?type=product&q=${encodeURIComponent(
      query
    )}&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await nativeFetch(url, { priority: "low" });

      if (!response.ok) {
        return;
      }

      const tempDoc = parseHtml(await response.text());
      const newRecentlyViewed = tempDoc.querySelector("recently-viewed-products");

      if (newRecentlyViewed && newRecentlyViewed.childElementCount > 0) {
        element.replaceChildren(...document.importNode(newRecentlyViewed, true).childNodes);
      } else {
        section.remove();
      }
    } catch (error) {
      console.warn("[Lustrabella patch] Recently viewed products failed to load.", error);
    }
  }

  function patchProductRecommendationsConstructor(Constructor) {
    if (!Constructor?.prototype || Constructor.prototype.__lustrabellaPatchedRecommendations) {
      return Constructor;
    }

    const originalConnectedCallback = Constructor.prototype.connectedCallback;

    Constructor.prototype.connectedCallback = function patchedConnectedCallback() {
      if (window.Shopify?.designMode && typeof originalConnectedCallback === "function") {
        return originalConnectedCallback.call(this);
      }

      runWhenNearViewport(
        this,
        () => lazyLoadProductRecommendations(this),
        "900px 0px"
      );
    };

    Object.defineProperty(Constructor.prototype, "__lustrabellaPatchedRecommendations", {
      value: true
    });

    return Constructor;
  }

  function patchRecentlyViewedConstructor(Constructor) {
    if (!Constructor?.prototype || Constructor.prototype.__lustrabellaPatchedRecentlyViewed) {
      return Constructor;
    }

    const originalConnectedCallback = Constructor.prototype.connectedCallback;

    Constructor.prototype.connectedCallback = function patchedConnectedCallback() {
      if (window.Shopify?.designMode && typeof originalConnectedCallback === "function") {
        return originalConnectedCallback.call(this);
      }

      runWhenNearViewport(
        this,
        () => lazyLoadRecentlyViewedProducts(this),
        "900px 0px"
      );
    };

    Object.defineProperty(Constructor.prototype, "__lustrabellaPatchedRecentlyViewed", {
      value: true
    });

    return Constructor;
  }

  function patchConstructorByName(name, Constructor) {
    if (name === "product-recommendations") {
      return patchProductRecommendationsConstructor(Constructor);
    }

    if (name === "recently-viewed-products") {
      return patchRecentlyViewedConstructor(Constructor);
    }

    return Constructor;
  }

  window.customElements.define = function patchedCustomElementDefine(name, Constructor, options) {
    const PatchedConstructor = patchConstructorByName(name, Constructor);
    return nativeCustomElementsDefine(name, PatchedConstructor, options);
  };

  function patchAlreadyDefinedElements() {
    const productRecommendationsConstructor = nativeCustomElementsGet("product-recommendations");
    const recentlyViewedConstructor = nativeCustomElementsGet("recently-viewed-products");

    if (productRecommendationsConstructor) {
      patchProductRecommendationsConstructor(productRecommendationsConstructor);
    }

    if (recentlyViewedConstructor) {
      patchRecentlyViewedConstructor(recentlyViewedConstructor);
    }
  }

  patchAlreadyDefinedElements();

})();
